import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './contact.css';

const Contact = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    from_name: '',
    from_email: '',
    message: ''
  });

  const [status, setStatus] = useState({
    loading: false,
    success: false,
    error: false,
    message: ''
  });

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.from_name || !formData.from_email || !formData.message) {
      setStatus({
        loading: false,
        success: false,
        error: true,
        message: 'Please fill in all fields'
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.from_email)) {
      setStatus({
        loading: false,
        success: false,
        error: true,
        message: 'Please enter a valid email address'
      });
      return;
    }

    setStatus({ loading: true, success: false, error: false, message: '' });

    try {
      const response = await fetch('https://664pzwrn97.execute-api.us-east-1.amazonaws.com/default/ContactFormHandler', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStatus({
          loading: false,
          success: true,
          error: false,
          message: 'Thank you! Your message has been sent successfully.'
        });

        // Clear form
        setFormData({
          from_name: '',
          from_email: '',
          message: ''
        });

        // Close modal after 2 seconds
        setTimeout(() => {
          onClose();
          setStatus({ loading: false, success: false, error: false, message: '' });
        }, 2000);
      } else {
        throw new Error(data.message || 'Failed to send message');
      }

    } catch (error) {
      console.error('Error sending message:', error);
      setStatus({
        loading: false,
        success: false,
        error: true,
        message: 'Oops! Something went wrong. Please try again or email us directly at info@rocksoftware.io'
      });
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="modal-container"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            <button className="modal-close" onClick={onClose} aria-label="Close">
              ✕
            </button>

            <div className="contact-header">
              <h2 className="contact-title">Get In Touch</h2>
              <p className="contact-description">
                Have a project in mind? Let's discuss how we can help you achieve your goals.
              </p>
            </div>

            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="from_name">Your Name</label>
                <input
                  type="text"
                  id="from_name"
                  name="from_name"
                  value={formData.from_name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  disabled={status.loading}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="from_email">Your Email</label>
                <input
                  type="email"
                  id="from_email"
                  name="from_email"
                  value={formData.from_email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  disabled={status.loading}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your project..."
                  rows="6"
                  disabled={status.loading}
                  required
                />
              </div>

              {status.message && (
                <div className={`form-message ${status.success ? 'success' : 'error'}`}>
                  {status.message}
                </div>
              )}

              <button
                type="submit"
                className="btn-submit"
                disabled={status.loading}
              >
                {status.loading ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default Contact;
